import { FormBuilderRef } from '@tn/formbuilder/hooks/useFormBuilder';
import {
    AnyEventObject,
    AnyStateConfig,
    assertEvent,
    DoneActorEvent,
    enqueueActions,
    ErrorActorEvent,
    Mapper,
    OutputFrom,
    setup,
} from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';
import { getEvaluatedRoute } from '../helpers';
import { submitFormTvInitActor } from '../tv/index';

export type ActivateFlowContext = {
    activationCode?: string;
    formBuilderRef: FormBuilderRef | null;
    success: boolean;
    url: string;
    hasConfirmedAccount?: boolean;
    device?: {
        /**
         * Human-readable device name, e.g. "Roku", "FireTV".
         * Prefer `displayName` for new code; `name` is kept for backwards compatibility.
         */
        name?: string;
        displayName?: string;
        /**
         * Partner identifier, e.g. "roku", "amazon".
         * Prefer `partnerId` for new code; `partner` is kept for backwards compatibility.
         */
        partner?: string;
        partnerId?: string;
    };
};

export const submitFormActivateActor: FlowEntity = {
    initActor: submitFormTvInitActor,
    inputActor: <
        Mapper<ActivateFlowContext, AnyEventObject, { activationCode: string }, AnyEventObject>
    >(({ context: { activationCode } }) => {
        return { activationCode };
    }),
};

export const activateFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, router } = rawParams as InitMachineParams;
        const machine = setup({
            types: {
                context: {} as ActivateFlowContext,
                events: {} as
                    | {
                          type: 'SUBMIT_CLICK';
                          params: ActivateFlowContext;
                      }
                    | {
                          type: 'CONNECT_FORMBUILDER_REF';
                          params: FormBuilderRef;
                      }
                    | { type: 'AUTO_SUBMIT'; params: { activationCode: string } }
                    | { type: 'ALREADY_LOGGED_IN' }
                    | { type: 'SUBMITTING' }
                    | DoneActorEvent<
                          OutputFrom<ReturnType<typeof submitFormActivateActor.initActor>>,
                          'submitting'
                      >
                    | ErrorActorEvent<
                          OutputFrom<ReturnType<typeof submitFormActivateActor.initActor>>,
                          'submitting'
                      >,
            },
            guards: {
                hasConfirmedAccount: ({ context }) => context?.hasConfirmedAccount === true,
                hasActivationCode: ({ context }) => !!context?.activationCode,
                shouldAutoSubmit: ({ context }) =>
                    !!context?.activationCode && context?.hasConfirmedAccount === true,
            },
            actions: {
                goToActivateRoute: enqueueActions(({ context }) => {
                    const evaluatedUrl = getEvaluatedRoute(router.pathname, context.url);
                    if (evaluatedUrl) {
                        router.push(evaluatedUrl);
                    }
                }),
                submitForm: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, ['SUBMIT_CLICK', 'AUTO_SUBMIT']);
                    context.formBuilderRef?.toggleLoading(true);
                    enqueue.assign({ ...event.params });
                    enqueue.raise({ type: 'SUBMITTING' });
                }),
                saveDataOnActivationSuccess: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'xstate.done.actor.submitting');
                    const doneEvent = event as DoneActorEvent<
                        OutputFrom<ReturnType<typeof submitFormTvInitActor>>,
                        'submitting'
                    >;
                    enqueue.assign({
                        success: true,
                        device: doneEvent.output?.device,
                    });
                    context.formBuilderRef?.toggleLoading(false);
                }),
                assignFormBuilderRef: enqueueActions(({ context, enqueue, event }) => {
                    assertEvent(event, 'CONNECT_FORMBUILDER_REF');
                    enqueue.assign({ formBuilderRef: event.params });

                    // Auto-submit only if user has explicitly confirmed their account
                    // This ensures auto-submit happens after confirmation, not on fresh page load
                    // activationCode is already passed to TV actor via inputActor and form is initialized with it
                    if (context?.activationCode && context?.hasConfirmedAccount === true) {
                        enqueue.raise({
                            type: 'AUTO_SUBMIT',
                            params: { activationCode: context.activationCode },
                        });
                    }
                }),
                handleError: ({ event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');
                    context.formBuilderRef?.toggleLoading(false);
                    context.formBuilderRef?.toggleError(event.error.message);
                },
                handleErrorWithBanner: ({ event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');
                    context.formBuilderRef?.toggleLoading(false);
                    context.formBuilderRef?.toggleFormError(
                        'activationCode',
                        'ssa_activation_code_incorrect_error',
                    );
                },
            },
            actors,
        }).createMachine({
            ...(config as AnyStateConfig),
            context: ({ input }) => ({
                activationCode: '',
                url: '',
                success: false,
                formBuilderRef: null,
                hasConfirmedAccount: false,
                ...input,
            }),
        });

        return machine;
    },

    inputActor: <
        Mapper<
            ActivateFlowContext,
            AnyEventObject,
            { activationCode?: string; hasConfirmedAccount?: boolean },
            AnyEventObject
        >
    >(({ context }) => ({
        activationCode: context?.activationCode,
        hasConfirmedAccount: context?.hasConfirmedAccount,
    })),

    outputActor: <Mapper<ActivateFlowContext, AnyEventObject, ActivateFlowContext, AnyEventObject>>(
        (({ context }) => {
            return { ...context };
        })
    ),
};
